/**
 * ActivityTab — unified, paged log of every marketing send (newsletter,
 * campaign automation, direct/bulk email, SMS) with open/click engagement.
 * Data: queryMyMarketing recent_sends.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  HStack,
  Icon,
  Badge,
  Tag,
  Button,
  Flex,
  Skeleton,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiMail,
  FiMessageSquare,
  FiEye,
  FiMousePointer,
  FiChevronLeft,
  FiChevronRight,
  FiInbox,
} from 'react-icons/fi';
import moment from 'moment';
import useMyMarketing from '../../../../hooks/useMyMarketing';

const PAGE_SIZE = 25;

const SOURCE_META = {
  newsletter: { label: 'Newsletter', colorScheme: 'blue' },
  campaign: { label: 'Campaign', colorScheme: 'orange' },
  direct: { label: 'Direct', colorScheme: 'gray' },
};

export default function ActivityTab({ uid }) {
  const { fetchRecentSends } = useMyMarketing(uid);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const headerBg = useColorModeValue('gray.50', 'gray.750');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (targetPage) => {
    setLoading(true);
    try {
      const result = await fetchRecentSends({ page: targetPage, pageSize: PAGE_SIZE });
      setRows(result.rows || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('[ActivityTab] Load failed:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fetchRecentSends]);

  // Back to page 1 when an admin switches which user they're viewing.
  useEffect(() => {
    setPage(0);
  }, [uid]);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" shadow="sm" overflow="hidden" data-tour="mm-activity-table">
      {loading ? (
        <Box p={5}>
          {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} h="34px" mb={2} borderRadius="md" />)}
        </Box>
      ) : rows.length === 0 ? (
        <Flex direction="column" align="center" py={16} gap={2}>
          <Icon as={FiInbox} boxSize={9} color="gray.300" />
          <Text fontSize="sm" color={mutedColor}>
            No marketing sends yet — activity from newsletters, campaigns, and bulk email lands here.
          </Text>
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table size="sm">
            <Thead bg={headerBg}>
              <Tr>
                <Th fontSize="10px">Sent</Th>
                <Th fontSize="10px">Channel</Th>
                <Th fontSize="10px">Source</Th>
                <Th fontSize="10px">Recipient</Th>
                <Th fontSize="10px">Subject / Message</Th>
                <Th fontSize="10px">Engagement</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => {
                const source = SOURCE_META[row.source] || SOURCE_META.direct;
                return (
                  <Tr key={row.id}>
                    <Td whiteSpace="nowrap">
                      <Tooltip label={moment(row.createdAt).format('MMM D, YYYY h:mm A')} hasArrow fontSize="xs">
                        <Text fontSize="xs">{moment(row.createdAt).fromNow()}</Text>
                      </Tooltip>
                    </Td>
                    <Td>
                      <HStack spacing={1.5}>
                        <Icon
                          as={row.channel === 'sms' ? FiMessageSquare : FiMail}
                          boxSize={3.5}
                          color={row.channel === 'sms' ? 'teal.400' : 'blue.400'}
                        />
                        <Text fontSize="xs" textTransform="uppercase">{row.channel}</Text>
                      </HStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={source.colorScheme} variant="subtle" fontSize="10px">
                        {source.label}
                      </Badge>
                    </Td>
                    <Td maxW="200px">
                      <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
                        {row.contactName || '—'}
                      </Text>
                      <Text fontSize="10px" color={mutedColor} noOfLines={1}>{row.to || ''}</Text>
                    </Td>
                    <Td maxW="320px">
                      <Text fontSize="xs" noOfLines={1}>
                        {row.subject || row.content || ''}
                      </Text>
                    </Td>
                    <Td whiteSpace="nowrap">
                      {row.channel === 'email' ? (
                        <HStack spacing={1.5}>
                          {row.openCount > 0 && (
                            <Tag size="sm" colorScheme="green" variant="subtle" fontSize="10px">
                              <Icon as={FiEye} boxSize={2.5} mr={1} />{row.openCount}
                            </Tag>
                          )}
                          {row.clickCount > 0 && (
                            <Tag size="sm" colorScheme="purple" variant="subtle" fontSize="10px">
                              <Icon as={FiMousePointer} boxSize={2.5} mr={1} />{row.clickCount}
                            </Tag>
                          )}
                          {row.openCount === 0 && row.clickCount === 0 && (
                            <Text fontSize="10px" color={mutedColor}>Not opened yet</Text>
                          )}
                        </HStack>
                      ) : (
                        <Text fontSize="10px" color={mutedColor}>—</Text>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination */}
      <Flex
        data-tour="mm-activity-pagination"
        justify="space-between"
        align="center"
        px={4}
        py={3}
        borderTopWidth="1px"
        borderColor={borderColor}
      >
        <Text fontSize="xs" color={mutedColor}>
          {total} send{total === 1 ? '' : 's'} · page {page + 1} of {totalPages}
        </Text>
        <HStack spacing={2}>
          <Button
            size="xs"
            variant="outline"
            leftIcon={<FiChevronLeft />}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            isDisabled={page === 0 || loading}
          >
            Previous
          </Button>
          <Button
            size="xs"
            variant="outline"
            rightIcon={<FiChevronRight />}
            onClick={() => setPage((p) => p + 1)}
            isDisabled={page + 1 >= totalPages || loading}
          >
            Next
          </Button>
        </HStack>
      </Flex>
    </Box>
  );
}
